// @ts-ignore
import { ChartData } from "../dataset-window/types";
import { DatasetLoadingConf, IDataSet } from "./interfaces";
import {addToDate, truncateDate} from "../../../utils/time-utils";

export function getDatasetDataInfo(identifiers: Array<String>, from: Date, to: Date) {
  return fetch(window.contextRoot + "/rest/datasetData/dataInfoRange?" + new URLSearchParams({ from: from.toISOString(), to: to.toISOString() } as any), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(identifiers),
  })
    .then((res) => {
      if (res.ok) {
        return res.json();
      } else {
        throw new Error();
      }
    })
    .catch((e) => {
      console.log(e);
    });
}

export function defaultFromDateTime(props: {loadConf?: DatasetLoadingConf, referenceTime?: string}) {
  const align = props.loadConf?.alignUnit;
  const lag = props.loadConf?.lag ?? 6;
  const lagUnit = props.loadConf?.lagUnit ?? "HOURS";

  const startingDate = props.referenceTime? new Date(props.referenceTime) : new Date()

  const date = truncateDate(startingDate, align);

  return addToDate(date, -lag, lagUnit);
}

export function defaultToDateTime(props: {loadConf?: DatasetLoadingConf, referenceTime?: string}) {
  const align = props.loadConf?.alignUnit;
  const lead = props.loadConf?.lead ?? 0;
  const leadUnit = props.loadConf?.leadUnit ?? "HOURS";

  const startingDate = props.referenceTime? new Date(props.referenceTime) : new Date()

  const date = truncateDate(startingDate, align);

  return addToDate(addToDate(date, lead, leadUnit), 1, "MINUTES");
}

export const convertStringDateToDate = (dataArray: Array<any>) => {
  return dataArray.map((d) => {
    return new Date(d["key"]);
  });
};

export const convertValue = (dataArray: Array<any>) => {
  return dataArray?.map((d) => {
    return d["value"];
  });
};

export const convertKey = (dataArray: Array<any>) => {
  return dataArray.map((d) => {
    return d["key"];
  });
};

export const convertDataToChartsData = (datasetArray: Array<IDataSet>): Array<ChartData> => {
  return datasetArray.map((d) => {
    return {
      id: d.identifier,
      title: d.title.replace(/_/g, " "),
      unit: d.unit,
      keyArr: convertKey(d.data),
      valueArr: convertValue(d.data),
    };
  });
};

export const isElementInDom = (id: string) => {
  const elementExists = document.getElementById(id);
  return !!elementExists;
};

export const round2decimals = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const hexToRgb = (hex: string) => {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  let colorRGB = null;
  if (result !== null) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    colorRGB = `rgba(${r},${g},${b},0.7)`;
  }
  return colorRGB ? colorRGB : null;
};

export const getAllDatasetsTitles = (dataArr: Array<any>) => {
  return dataArr.map((d) => {
    return { title: `${d.title.replace(/_/g, " ")}, ${d.unit}`, field: d.identifier };
  });
};

const sortAscDate = (arr: Array<any>) => {
  return arr.sort((objA, objB) => Number(new Date(objA)) - Number(new Date(objB)));
};

const sortAscDateTimestamp = (arr: Array<any>) => {
  return arr.sort((objA, objB) => Number(new Date(objA.timestamp)) - Number(new Date(objB.timestamp)));
};

const sortMapAscDate = (map: Map<string, any>) => {
  const mapSorted = new Map([...map.entries()].sort());
  return mapSorted;
};

const formatTimestamp = (timestamp: string) => {
  return  new Date(timestamp).toLocaleString("lt-LT")
}

export const getAllDatasetsTimestamps = (data: Array<any>) => {
  const startDate = new Date();
  let existingDatasets: Array<string> = data.map((d) => `${d.title.replace(/_/g, " ")}, ${d.unit}`);
  let tableTemplate: Array<any> = [];
  const previousDatasets: Array<string> = [];
  //FOR EACH DATASET
  for (const dataset of data) {
    const datasetValues: any = dataset.data;
    const datasetName: string = `${dataset.title.replace(/_/g, " ")}, ${dataset.unit}`;
    previousDatasets.push(datasetName);
    //FOR EACH VALUE OF CURRENT DATASET
    for (const { key, value } of datasetValues) {
      let updatedRow: any = {};
      //ALL DATASETS FOR SETTING PROPERTIES
      for (const prev of existingDatasets) {
        //EMPTY INITIAL VALUES FOR DATASETS ON CURRENT TIME
        updatedRow = { timestamp: formatTimestamp(key), [prev]: "", ...updatedRow };
      }
      const found = tableTemplate.findIndex((t) => t.timestamp === key); // binarySearchIndex(tableTemplate, key )
      if (found < 0) {
        //NOT FOUND, push NEW ROW
        updatedRow = { ...updatedRow, timestamp:  formatTimestamp(key), [datasetName]: value };
        tableTemplate.push(updatedRow);
      } else {
        //FOUND APPEND ROW WITH PROPERTY and NEW VALUE
        updatedRow = { ...tableTemplate[found], [datasetName]: value };
        tableTemplate[found] = updatedRow;
      }
    }
  }
  const endDate = new Date();
  const duration = endDate.getTime() - startDate.getTime();
  const result = sortAscDateTimestamp(tableTemplate);;
  //console.log("DURATION OLD", duration);
  return result
};

export const mapDataTable = (data: Array<IDataSet>) => {
  const startDate = new Date();
  const timestampSet = new Set();
  for (const dataset of data) {
    for (const d of dataset.data) {
      timestampSet.add(d.key)
    }
  }
  const timestampsSortedArray: Array<Date> = sortAscDate([...timestampSet]);
  const table: Array<any> = [];
  for (const timestamp of timestampsSortedArray) {
    let currentRow = { timestamp: new Date(timestamp).toLocaleString("lt-LT") };
    for (const dataset of data) {
      const datasetName: string = `${dataset.title.replace(/_/g, " ")}, ${dataset.unit}`;
      const found = dataset.data.find((d: { key: Date }) => d.key === timestamp);
      if (found == undefined) {
        currentRow = { ...currentRow, [datasetName]: "" };
      } else {
        currentRow = { ...currentRow, [datasetName]: found.value.toFixed(3) };
      } 
    }
    table.push(currentRow);
  }
  const endDate = new Date();
  const duration = endDate.getTime() - startDate.getTime();
  //console.log("DURATION NEW", duration);
  return table;
};
